<script setup>
import { ref, watch, computed, nextTick, onMounted, onBeforeUnmount, provide } from 'vue'
import { useFloating, autoUpdate, offset, flip, shift } from '@floating-ui/vue'
import { onClickOutside, useEventListener } from '@vueuse/core'
import { useUserStore } from '@/stores/user.js'
import { storeToRefs } from 'pinia'
import chatMessageContextStore from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Store/chat-message-context-store.js'
import ChatMessageMenuTranslateButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuTranslateButton.vue'
import ChatMessageMenuCopyButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuCopyButton.vue'
import ChatMessageMenuDeleteButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuDeleteButton.vue'
import ChatMessageMenuSaveButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuSaveButton.vue'
import ChatMessageMenuReportButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuReportButton.vue'
import ChatMessageMenuReactions from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuReactions.vue'
import ChatMessageMenuReplyButton from '@/components/Chat/ChatMessages/Includes/ChatMessageMenu/Includes/ChatMessageMenuReplyButton.vue'

const props = defineProps({
  message: {
    type: Object,
    required: true
  }
})

provide('message', props.message)

const open = ref(false)

const trigger = ref(null)
const floating = ref(null)

const {
  active
} = chatMessageContextStore

const menuId = 'msg-menu-' + Math.random().toString(36).slice(2)

// Point where user clicked
const point = ref({ x: 0, y: 0 })

const virtualReference = computed(() => ({
  getBoundingClientRect () {
    const { x, y } = point.value
    return new DOMRect(x, y, 0, 0)
  },
  contextElement: trigger.value ?? document.body
}))

const { floatingStyles, update } = useFloating(
  ref(virtualReference.value),
  floating,
  {
    open,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(6), flip(), shift()]
  }
)

function openAtPoint (x, y) {
  point.value = { x, y }
  open.value = true
  active.value = true
}

function isNoContextTarget (e) {
  const t = e.target
  return t && typeof t.closest === 'function' && t.closest('.no-context')
}

const isMobile = window.matchMedia('(pointer: coarse)').matches

function onLeftClick (e) {
  if (isNoContextTarget(e) || !isMobile || active.value) {
    return
  }

  openAtPoint(e.clientX, e.clientY)
}

function onContextMenu (e) {
  if (isNoContextTarget(e)) {
    return
  }

  e.preventDefault()
  openAtPoint(e.clientX, e.clientY)
}

async function close () {
  active.value = true
  open.value = false
  await new Promise((resolve) => setTimeout(resolve, 300))
  active.value = false
}

provide('close', close)

onClickOutside(floating, () => {
  close()
})

function isEventInside (el, e) {
  if (!el) return false
  const target = e.target
  const path = typeof e.composedPath === 'function' ? e.composedPath() : null

  if (path && path.length) {
    return path.some(n => n === el || (n instanceof Node && el.contains(n)))
  }

  return target ? el.contains(target) : false
}

const stopContextOutside = useEventListener(document, 'contextmenu', (e) => {
  if (!open.value) {
    return
  }

  const inFloating = isEventInside(floating.value, e)
  const inTrigger = isEventInside(trigger.value, e)

  if (!inFloating && !inTrigger) {
    close()
  }
})

function onKeydownWindow (e) {
  if (e.key === 'Escape') {
    close()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onKeydownWindow)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydownWindow)
  detachScrollListeners()
  stopContextOutside && stopContextOutside()
})

watch(open, async (v) => {
  if (v) {
    attachScrollListeners()
    await nextTick()
    update()
  }
  else {
    detachScrollListeners()
  }
})

function onAnyScroll () {
  close()
}

function attachScrollListeners () {
  document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true })
  window.addEventListener('wheel', onAnyScroll, { passive: true })
  window.addEventListener('touchmove', onAnyScroll, { passive: true })
}

function detachScrollListeners () {
  document.removeEventListener('scroll', onAnyScroll, { capture: true })
  window.removeEventListener('wheel', onAnyScroll)
  window.removeEventListener('touchmove', onAnyScroll)
}

const {
  options,
  userId
} = storeToRefs(useUserStore())

const isLoggedUser = computed(() => userId.value !== null && userId.value === props.message?.senderId)

const emit = defineEmits(['translate'])

provide('translate', (payload) => {
  emit('translate', payload)
})
</script>

<template>
  <div
    ref="trigger"
    :aria-controls="menuId"
    aria-haspopup="menu"
    :aria-expanded="open"
    @click="onLeftClick"
    @contextmenu="onContextMenu">
    <slot/>
  </div>

  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="open"
        :id="menuId"
        ref="floating"
        role="menu"
        class="flex flex-col gap-2 z-10 pointer-events-none"
        :style="floatingStyles">
        <ChatMessageMenuReactions/>

        <div class="drop-shadow-lg pointer-events-auto flex flex-col px-2 bg-white w-full max-w-36 mx-auto rounded-lg divide-y divide-gray-100">
          <ChatMessageMenuReplyButton/>

          <ChatMessageMenuCopyButton
            v-if="message.text"/>

          <ChatMessageMenuTranslateButton
            v-if="options.messageTranslationEnabled && !isLoggedUser && message.text"/>

          <ChatMessageMenuSaveButton
            v-if="(message.type === 3) && !(message.status === 0 || (message.attachment.paid === 0 && message.attachment.price)) && !(message.ttl || message.ttlAfterRead)"
            :message="message"/>

          <ChatMessageMenuReportButton
            v-if="!isLoggedUser && options.abuseReportingEnabled"
            :message="message"/>

          <ChatMessageMenuDeleteButton/>
        </div>
      </div>
    </transition>
  </Teleport>
</template>